import React, { Component } from "react";

/**
 * The controls a page is built from: a field with its label above it, the
 * toolbar over a table, and the delete that asks inside the row instead of
 * throwing a dialog over the page.
 *
 * Presentation only. Every one of these reads props and calls back -- none of
 * them fetches, filters or deletes anything itself.
 */

/**
 * A form field. The label sits above the input and stays readable when the
 * field is full, and the hint line has a place of its own.
 *
 *   <Field label="إسم الدرس" hint="هيظهر لطلابك كده بالظبط">
 *     <InputText value={...} onChange={...} />
 *   </Field>
 *
 *   <Field label="رقم الموبايل" error="الرقم لازم يبدأ بـ 01">...</Field>
 */
export const Field = ({ label, hint, error, required = false, htmlFor, children }) => (
  <label className={error ? "ui-field is-bad" : "ui-field"} htmlFor={htmlFor}>
    {label ? (
      <span className="ui-label">
        {label}
        {required ? <em> *</em> : null}
      </span>
    ) : null}

    {children}

    {error || hint ? <span className="ui-hint">{error || hint}</span> : null}
  </label>
);

/**
 * The strip above a table: a search box, whatever narrows the table, and the
 * count.
 *
 * The filters sit ON the bar. There is no drawer and no toggle: a door in
 * front of a doorway only hides the one dropdown behind it and then needs a
 * second row of pills to say what it is hiding. The dropdown shows its own
 * value, so it is its own indicator.
 *
 *   <Toolbar
 *     value={q} onSearch={(v) => this.setState({ q: v })}
 *     count={`${rows.length} نتيجة`}
 *     filters={<>…dropdowns…</>}
 *     action={{ label: "إضافة حصة", onClick: openAdd }}
 *   />
 *
 * `active` / `onClear` are still accepted so the pages passing them keep
 * working, but nothing is drawn for them -- a chosen filter is already
 * visible in the control that chose it.
 */
export class Toolbar extends Component {
  render() {
    const {
      value,
      onSearch,
      placeholder = "ابحث...",
      count,
      tools,
      filters,
      active = [],
      onClear,
      action,
    } = this.props;

    return (
      <>
        <div className="ui-toolbar">
          <span className="ui-search">
            <i className="pi pi-search"></i>
            <input
              type="search"
              value={value}
              placeholder={placeholder}
              onChange={(e) => onSearch && onSearch(e.target.value)}
            />
          </span>

          {/* tabs, segmented controls -- anything that is not a filter but
              still belongs on the bar */}
          {tools ? <span className="ui-tools">{tools}</span> : null}

          {/* the filters themselves, in the place the toggle used to hold */}
          {filters ? <span className="ui-tfilters">{filters}</span> : null}

          {count ? <span className="ui-count">{count}</span> : null}

          {action ? (
            <button
              type="button"
              className="ui-btn is-gold"
              style={{ marginInlineStart: "auto" }}
              onClick={action.onClick}
            >
              <i className={action.icon || "pi pi-plus"}></i>
              <span>{action.label}</span>
            </button>
          ) : null}
        </div>
      </>
    );
  }
}

/**
 * Delete, asked where the row is. The bin turns into "تحذفها؟ أيوه / لأ" in
 * the same cell, so the eye never leaves the line it is about to lose.
 *
 * Use it for the quick, repeated deletes; keep <Confirm> for the ones that
 * take something big with them.
 *
 *   <InlineConfirm busy={removingId === row.id} onConfirm={() => remove(row.id)} />
 */
export class InlineConfirm extends Component {
  state = { asking: false };

  render() {
    const { onConfirm, busy = false, label = "تحذفها؟" } = this.props;

    if (busy) {
      return (
        <span className="ui-rowask">
          <i className="pi pi-spin pi-spinner"></i>
        </span>
      );
    }

    if (!this.state.asking) {
      return (
        <button
          type="button"
          className="ui-icon-btn is-danger"
          aria-label="حذف"
          onClick={() => this.setState({ asking: true })}
        >
          <i className="pi pi-trash"></i>
        </button>
      );
    }

    return (
      <span className="ui-rowask">
        <b>{label}</b>
        <button
          type="button"
          className="ui-btn is-danger"
          onClick={() => {
            this.setState({ asking: false });
            onConfirm && onConfirm();
          }}
        >
          أيوه
        </button>
        <button type="button" className="ui-btn is-secondary" onClick={() => this.setState({ asking: false })}>
          لأ
        </button>
      </span>
    );
  }
}

/**
 * The shortcuts that sit beside a calendar. Hand it to PrimeReact's Calendar
 * through its `panelFooterTemplate`/`footerTemplate`, or render it next to a
 * Calendar inside the same panel.
 *
 *   <DateShortcuts value={range} onPick={(key) => ...} />
 */
export const DateShortcuts = ({ value, onPick, items }) => {
  const list = items || [
    { key: "today", label: "النهاردة" },
    { key: "7d", label: "آخر ٧ أيام" },
    { key: "month", label: "الشهر ده" },
    { key: "term", label: "الترم" },
  ];

  return (
    <div className="ui-dates">
      {list.map((it) => (
        <button
          type="button"
          key={it.key}
          aria-selected={value === it.key}
          onClick={() => onPick && onPick(it.key)}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
};

/**
 * What an open menu says while it has nothing to show: it is either waiting
 * for its options or there are none. Hand it to a PrimeReact Dropdown or
 * MultiSelect as `emptyMessage` -- the component decides when to show it,
 * exactly as it already did with the string it used to be given.
 *
 *   <Dropdown ... emptyMessage={<SelectEmpty loading={this.state.loadingMaterials} />} />
 */
export const SelectEmpty = ({ loading = false, text = "مفيش عناصر هنا" }) => (
  <span className={loading ? "ui-selempty is-wait" : "ui-selempty"}>
    {loading ? <i className="ui-selempty-spin" aria-hidden="true"></i> : null}
    <span>{loading ? "جارٍ التحميل…" : text}</span>
  </span>
);

/** Filter tabs over a table, or sections inside a page. */
export const Segmented = ({ value, onChange, items }) => (
  <div className="ui-segs">
    {items.map((it) => (
      <button
        type="button"
        key={it.key}
        aria-pressed={value === it.key}
        onClick={() => onChange && onChange(it.key)}
      >
        {it.label}
      </button>
    ))}
  </div>
);

export const Tabs = ({ value, onChange, items }) => (
  <div className="ui-tabs">
    {items.map((it) => (
      <button
        type="button"
        key={it.key}
        aria-pressed={value === it.key}
        onClick={() => onChange && onChange(it.key)}
      >
        {it.label}
      </button>
    ))}
  </div>
);
